/**
 * PATH: packages/quote-builder/src/grid/refInsert.ts
 * FILE_ID: QB-GRID-REFINSERT-001
 * PURPOSE: Pure text transform for Excel-style formula composition —
 *   "type = then CLICK a cell" inserts that cell's A1 ref at the caret,
 *   Shift+click grows a range (A1:B3), Ctrl/Cmd+click appends comma refs.
 * WHY: Client (2026-07-03): "can't even click = and click a cell…".
 *   Recurrence (2026-07-16): multi-select for SUM failed — Shift+click was
 *   a no-op during formula compose, so =SUM(A1:A3) could never be built.
 * FLOW: (value, caret, ref, lastInsert?, mode) → { next, caret, lastInsert }
 */
import { expandRange, parseRef } from './a1'

/** Span of the most recent programmatic ref insertion into one input. */
export interface RefInsertSpan {
  start: number
  end: number
}

export interface RefInsertResult {
  next: string
  /** Caret position after the insert (collapsed). */
  caret: number
  /** Span to remember so an immediate next click replaces/extends this ref. */
  lastInsert: RefInsertSpan
  /** Anchor cell of the inserted token (for compose preview highlight). */
  previewAnchor: string
  /** End cell when the token is a range; null for a single cell. */
  previewEnd: string | null
}

export type FormulaRefMode = 'replace' | 'range' | 'append'

const RANGE_OR_CELL_RE = /^[A-Za-z]+\d+(?::[A-Za-z]+\d+)?$/

/** Normalise two cells to Excel-style `TopLeft:BottomRight` (or a single ref). */
export function formatA1Range(a: string, b: string): string {
  const cells = expandRange(a, b)
  if (cells.length === 0) {
    return parseRef(a) ? a.toUpperCase() : a
  }
  if (cells.length === 1) return cells[0]
  return `${cells[0]}:${cells[cells.length - 1]}`
}

/** First cell of a `B3` or `B3:D5` token. */
export function rangeAnchor(token: string): string | null {
  const part = token.split(':')[0]?.trim()
  if (!part || !parseRef(part)) return null
  return part.toUpperCase()
}

/** Last cell of a range token, or the cell itself. */
export function rangeEndCell(token: string): string | null {
  const parts = token.split(':')
  const end = (parts[1] ?? parts[0])?.trim()
  if (!end || !parseRef(end)) return null
  return end.toUpperCase()
}

function previewFromToken(token: string): { anchor: string; end: string | null } {
  const anchor = rangeAnchor(token) ?? token.toUpperCase()
  const end = rangeEndCell(token)
  return { anchor, end: end && end !== anchor ? end : null }
}

function priorRefToken(
  value: string,
  selStart: number,
  selEnd: number,
  lastInsert?: RefInsertSpan | null,
): { token: string; span: RefInsertSpan } | null {
  if (!lastInsert) return null
  if (selStart !== selEnd) return null
  if (selStart !== lastInsert.end) return null
  if (lastInsert.start < 0 || lastInsert.end > value.length) return null
  const token = value.slice(lastInsert.start, lastInsert.end)
  if (!RANGE_OR_CELL_RE.test(token)) return null
  return { token, span: lastInsert }
}

/**
 * Insert `ref` into `value` at the caret.
 *
 * - `replace` (default): consecutive clicks replace the prior ref (Excel).
 * - `range` (Shift+click): grow the prior ref into `anchor:ref`.
 * - `append` (Ctrl/Cmd+click): append `,ref` after the prior token.
 */
export function insertRefText(
  value: string,
  selStart: number,
  selEnd: number,
  ref: string,
  lastInsert?: RefInsertSpan | null,
  mode: FormulaRefMode = 'replace',
): RefInsertResult {
  const clicked = ref.toUpperCase()
  let start = Math.max(0, Math.min(selStart, value.length))
  let end = Math.max(start, Math.min(selEnd, value.length))
  const prior = priorRefToken(value, start, end, lastInsert)

  let token = clicked
  let spanStart = start
  let preview = { anchor: clicked, end: null as string | null }

  if (mode === 'range' && prior) {
    const anchor = rangeAnchor(prior.token) ?? clicked
    token = formatA1Range(anchor, clicked)
    start = prior.span.start
    end = prior.span.end
    spanStart = start
    preview = previewFromToken(token)
  } else if (mode === 'append' && prior) {
    token = `,${clicked}`
    start = prior.span.end
    end = prior.span.end
    // Remember only the newly appended cell so the next Shift+click ranges from it.
    spanStart = start + 1 // after the comma
    preview = { anchor: clicked, end: null }
  } else if (mode === 'replace' && prior) {
    // Replace prior cell OR prior range with the newly clicked cell.
    token = clicked
    start = prior.span.start
    end = prior.span.end
    spanStart = start
    preview = { anchor: clicked, end: null }
  } else {
    // Plain insert at caret (first click, or caret moved off the last span).
    token = clicked
    spanStart = start
    preview = { anchor: clicked, end: null }
  }

  const next = value.slice(0, start) + token + value.slice(end)
  const caret = start + token.length
  const spanEnd =
    mode === 'append' && prior ? caret : start + token.length

  return {
    next,
    caret,
    lastInsert: {
      start: mode === 'append' && prior ? spanStart : start,
      end: spanEnd,
    },
    previewAnchor: preview.anchor,
    previewEnd: preview.end,
  }
}

/** A formula is "composing" when it starts with '=' — only then does a
 *  grid click mean "reference that cell" rather than "move focus". */
export function isComposingFormula(value: string): boolean {
  return value.startsWith('=')
}
